/**
 * CacheKeyMatcher - Utility for matching cache keys against criteria
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for filtering and matching cache keys
 * based on document hash, page index, and other criteria.
 */

import { parsePageCacheKey } from './CacheKeyGenerator';

export type ParsedPageCacheKey = { docHash: string; pageIndex: number };

/**
 * Check if a cache key matches document hash and page index criteria
 * 
 * Parses a cache key and checks if it matches the given document hash
 * and if the page index is greater than or equal to the start page.
 * 
 * @param key - Cache key to check
 * @param docHash - Document hash to match
 * @param startPage - Minimum page index (inclusive)
 * @returns True if the key matches the criteria
 */
export function matchesCacheKeyCriteria(
  key: string,
  docHash: string,
  startPage: number
): boolean {
  const parsed = parsePageCacheKey(key);
  return parsed !== null && parsed.docHash === docHash && parsed.pageIndex >= startPage;
}

/**
 * Check if a parsed cache key matches document hash
 * 
 * @param parsed - Parsed cache key
 * @param docHash - Document hash to match
 * @returns True if the parsed key matches the document hash
 */
export function matchesDocHash(
  parsed: ParsedPageCacheKey | null,
  docHash: string
): parsed is ParsedPageCacheKey {
  return parsed !== null && parsed.docHash === docHash;
}

/**
 * Check if a parsed cache key's page index is within range
 * 
 * @param parsed - Parsed cache key
 * @param startPage - Minimum page index (inclusive)
 * @param endPage - Maximum page index (inclusive, optional)
 * @returns True if the page index is within range
 */
export function matchesPageRange(
  parsed: ParsedPageCacheKey | null,
  startPage: number,
  endPage?: number
): boolean {
  if (parsed === null) {
    return false;
  }
  
  if (endPage !== undefined) {
    return parsed.pageIndex >= startPage && parsed.pageIndex <= endPage;
  }
  
  return parsed.pageIndex >= startPage;
}

/**
 * Filter cache keys by document hash
 * 
 * Returns only keys that match the given document hash.
 * 
 * @param keys - Array of cache keys
 * @param docHash - Document hash to match
 * @returns Array of keys matching the document hash
 */
export function filterKeysByDocHash(keys: string[], docHash: string): string[] {
  return keys.filter(key => {
    const parsed = parsePageCacheKey(key);
    return matchesDocHash(parsed, docHash);
  });
}

